import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Spinner } from '../spinner/spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
}

const sizeStyles = {
  sm: css`
    height: 34px;
    padding: 0 14px;
    font-size: 13px;
    gap: 6px;
  `,
  md: css`
    height: 42px;
    padding: 0 18px;
    font-size: 14px;
    gap: 8px;
  `,
};

const variantStyles = {
  primary: css`
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryOn};
    box-shadow: ${({ theme }) => theme.shadow.sm};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryLight};
      transform: translateY(-1px);
    }
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.bgSurface};
    color: ${({ theme }) => theme.colors.textPrimary};
    border: 1px solid ${({ theme }) => theme.colors.border};

    &:hover:not(:disabled) {
      border-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.primary};
    }
  `,
  danger: css`
    background: ${({ theme }) => theme.colors.danger};
    color: #fff;

    &:hover:not(:disabled) {
      filter: brightness(1.05);
      transform: translateY(-1px);
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.textSecondary};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.bgMuted};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  `,
};

interface StyledButtonProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth?: boolean;
}

const Styled = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.md};
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all ${({ theme }) => theme.transitions.base};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  ${(props: StyledButtonProps) => sizeStyles[props.$size]}
  ${(props: StyledButtonProps) => variantStyles[props.$variant]}

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading,
      disabled,
      startIcon,
      endIcon,
      children,
      fullWidth,
      type = 'button',
      ...rest
    },
    ref,
  ) => (
    <Styled
      ref={ref}
      type={type}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner aria-hidden="true" /> : startIcon}
      {children}
      {!loading && endIcon}
    </Styled>
  ),
);

Button.displayName = 'Button';
